import { useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import CalendarBlank from "../../../../assets/svg-fc/CalendarBlank";
import CheckIconSvg from "../../../../assets/svg-fc/CheckIconSvg";
import ClockSvg from "../../../../assets/svg-fc/ClockSvg";
import CrossIconSvg from "../../../../assets/svg-fc/CrossIconSvg";
import MoneyIcon from "../../../../assets/svg-fc/MoneyIcon";
import UserBlank from "../../../../assets/svg-fc/UserBlank";
import palette from "../../../../theme/palette";
import IconWithText from "../../Icon-with-text/IconWithText";
import useTimer from "../../../../hooks/useTimer";
import "./prePaidCard.css";

type Props = {
  className?: string;
  sx?: SxProps<Theme>;
  title: string;
  declinedClick: (e: any) => void;
  completedClick: (e: any) => void;
  openCardDetail?: () => void;
  guestsCounter: string;
  date: string;
  AED: string;
  expireAt: string;
  onHold?: boolean;
  startTime?: string;
  eventRequestId: string;
  customerNotes: string;
};

const PrePaidCard = ({
  className,
  sx,
  title,
  declinedClick,
  completedClick,
  openCardDetail,
  guestsCounter,
  date,
  AED,
  expireAt,
  onHold,
  startTime = "",
  eventRequestId = "",
  customerNotes,
}: Props) => {
  const timeLeft = useTimer({ expireAt, eventRequestId });
  const noteRef = useRef<HTMLParagraphElement>(null);
  const [noteTruncated, setNoteTruncated] = useState(false);

  useLayoutEffect(() => {
    const note = noteRef.current;
    if (!note) {
      setNoteTruncated(false);
      return undefined;
    }
    const measure = () => setNoteTruncated(note.scrollHeight > note.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(note);
    return () => observer.disconnect();
  }, [customerNotes]);
  return (
    <Card
      sx={{
        p: 2,
        backgroundColor: "#ffffff",
        borderRadius: "0.5rem",
        borderStartStartRadius: ".75rem",
        borderEndStartRadius: ".75rem",
        borderLeft: `8px solid ${onHold ? "#E5A545" : "#789b73"}`,
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.10)",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        ...sx,
        "& h3": { fontSize: 18 },
      }}
      className={className}
      elevation={0}
    >
      <Typography
        variant="h3"
    onClick={openCardDetail} sx={{ color: palette.secondary.main, mb: 1.5, textTransform: "capitalize", fontWeight: 500, cursor: "pointer" }}>
        {title}
      </Typography>
      <Stack
    onClick={openCardDetail} sx={{ flexDirection: "row", flexWrap: "wrap", mb: 1.5, gap: 1.5, cursor: "pointer" }}>
        <IconWithText text={`${guestsCounter} Guests`} icon={<UserBlank />} />
        <IconWithText text={date} icon={<CalendarBlank />} />
        <IconWithText text={startTime} icon={<ClockSvg />} />
        <IconWithText text={`AED ${AED}`} icon={<MoneyIcon />} />
      </Stack>
      <Box className="customer-note" onClick={openCardDetail}>
        {customerNotes && (
          <>
            <p ref={noteRef} className="customer-note-clamp">
              {"Customer: "}
              <span className="customer-note-text">{`“${customerNotes}”`}</span>
            </p>
            {noteTruncated && (
              <span className="read-more-wrap">
                {"… "}
                <span className="read-more">Read more</span>
              </span>
            )}
          </>
        )}
      </Box>
    <Stack sx={{ flexDirection: "row", gap: 1.5, mt: "auto" }}>
        {
          <Chip
            label={onHold ? "Hold" : timeLeft}
            sx={{
              bgColor: onHold
                ? "rgba(229, 165, 69, 0.1)"
                : "rgba(180, 35, 24, 0.11)",
              color: onHold ? "#E5A545" : palette.error.main,
              fontSize: 12,
              fontWeight: 500,
              height: 28,
              px: 1,
            }}
          />
        }
    <Box sx={{ marginInlineStart: "auto", display: "flex", gap: 1.5 }}>
          <Button
            aria-label="cross"
            className="cross_icon"
            onClick={completedClick}
            variant="contained"
            disabled={onHold}
            sx={{
              minWidth: "auto",
              width: 32,
              height: 32,
              border: 1,
              borderRadius: "100rem",
              p: 0,
              ":hover": { color: palette.primary.main },
              "& svg": {
                width: 19,
                height: 19,
              },
            }}
          >
            <CheckIconSvg />
          </Button>
          <Button
            aria-label="cross"
            className="cross_icon"
            onClick={declinedClick}
            sx={{
              minWidth: "auto",
              width: 32,
              height: 32,
              backgroundColor: "#D32F2F",
              borderRadius: "100rem",
              "& svg": {
                color: palette.grey["0"],
                width: 10,
                height: 10,
              },
              ":hover": {
                border: 1,
                borderColor: "#D32F2F",
                "& svg": {
                  color: "#D32F2F",
                },
              },
            }}
          >
            <CrossIconSvg />
          </Button>
        </Box>
      </Stack>
    </Card>
  );
};

export default PrePaidCard;
